import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect, useSyncExternalStore } from "react"
import { qk } from "./api"
import { sdk } from "./sdk"
import type { Me } from "./types"

/**
 * Keel Admin colour theme: Light (the default), Dark, or System (follows the device).
 *
 * Each staff member's choice is saved on their account (POST /admin/terify/me { theme }) and
 * cached in this browser's localStorage. public/theme-init.js reads the cache before the app's
 * code loads and sets <html data-theme="light|dark"> (plus the `dark` class for the component
 * library), so a reload never flashes the wrong theme. When /admin/terify/me answers, the saved
 * choice wins over the cache (another device may have changed it).
 */
export type ThemePref = "light" | "dark" | "system"
export type ResolvedTheme = "light" | "dark"

/** Shared with public/theme-init.js: keep the key and colours in step. */
export const THEME_KEY = "keel.theme"
const META_COLOR: Record<ResolvedTheme, string> = { light: "#FFFFFF", dark: "#131E28" }
const QUERY = "(prefers-color-scheme: dark)"

export const THEME_OPTIONS: { value: ThemePref; label: string; hint: string }[] = [
  { value: "light", label: "Light", hint: "White background (default)" },
  { value: "dark", label: "Dark", hint: "Dark background, easier at night" },
  { value: "system", label: "System", hint: "Match this device's setting" },
]

const isPref = (v: unknown): v is ThemePref => v === "light" || v === "dark" || v === "system"

function readCache(): ThemePref {
  try {
    const v = window.localStorage.getItem(THEME_KEY)
    return isPref(v) ? v : "light"
  } catch {
    return "light"
  }
}

function writeCache(p: ThemePref) {
  try {
    window.localStorage.setItem(THEME_KEY, p)
  } catch {
    // Private mode / blocked storage: the account still remembers it.
  }
}

const systemDark = () => typeof window !== "undefined" && !!window.matchMedia?.(QUERY).matches

export function resolveTheme(p: ThemePref): ResolvedTheme {
  return p === "dark" || (p === "system" && systemDark()) ? "dark" : "light"
}

let pref: ThemePref = typeof window === "undefined" ? "light" : readCache()
let snapshot = { pref, resolved: resolveTheme(pref) }
const listeners = new Set<() => void>()

function apply() {
  const resolved = resolveTheme(pref)
  const root = document.documentElement
  root.setAttribute("data-theme", resolved)
  root.setAttribute("data-theme-pref", pref)
  root.classList.toggle("dark", resolved === "dark")
  root.style.colorScheme = resolved
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", META_COLOR[resolved])
  if (snapshot.pref !== pref || snapshot.resolved !== resolved) {
    snapshot = { pref, resolved }
    listeners.forEach((l) => l())
  }
}

/** Sets the theme in this tab (and its cache) right away; saving it to the account is separate. */
export function setThemePref(p: ThemePref) {
  pref = p
  writeCache(p)
  apply()
}

if (typeof window !== "undefined") {
  apply()
  // "System": follow the device when it switches (e.g. at sunset).
  window.matchMedia?.(QUERY).addEventListener?.("change", () => pref === "system" && apply())
  // Another tab changed it.
  window.addEventListener("storage", (e) => {
    if (e.key === THEME_KEY) {
      pref = readCache()
      apply()
    }
  })
}

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}

/** The current choice and what it resolves to (System → light or dark). */
export function useTheme() {
  return useSyncExternalStore(subscribe, () => snapshot, () => snapshot)
}

/** Applies the theme saved on the account once /admin/terify/me has answered. */
export function useAccountTheme(saved: ThemePref | undefined) {
  useEffect(() => {
    if (saved && isPref(saved) && saved !== pref) setThemePref(saved)
  }, [saved])
}

/** Changes the theme now and saves it to the signed-in staff member's account. */
export function useSetTheme() {
  const qc = useQueryClient()
  const save = useMutation({
    mutationFn: (theme: ThemePref) => sdk.client.fetch<Me>("/admin/terify/me", { method: "POST", body: { theme } }),
    onSuccess: (me) => qc.setQueryData(qk.me, me),
  })
  return {
    set: (theme: ThemePref) => {
      setThemePref(theme)
      save.mutate(theme)
    },
    saving: save.isPending,
    error: save.error,
  }
}
